import { useEffect } from 'react';

interface LatLngNew {
  lat: number;
  lng: number;
}

interface ImageOverlayProps {
  map: google.maps.Map | null;
  polygon: LatLngNew[]; // 4 corners in order
  imageUrl: string;
  opacity?: string;
  rotation?: number; // rotation of the div
}

export function ImageOverlay({ map, polygon, imageUrl, opacity, rotation = 0 }: ImageOverlayProps) {
  useEffect(() => {
    if (!map || polygon.length !== 4) return;

    class CustomOverlay extends google.maps.OverlayView {
      private div_: HTMLDivElement | null = null;

      onAdd() {
        this.div_ = document.createElement('div');
        this.div_.style.position = 'absolute';
        this.div_.style.transformOrigin = 'center center';
        const img = document.createElement('img');
        img.src = imageUrl;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.position = 'absolute';
        if (opacity) img.style.opacity = opacity;

        this.div_.appendChild(img);
        this.getPanes()?.overlayLayer.appendChild(this.div_);
      }

      draw() {
        if (!this.div_) return;

        const projection = this.getProjection();
        if (!projection) return;

        // Project polygon corners to pixels
        const pts = polygon.map((p) => projection.fromLatLngToDivPixel(new google.maps.LatLng(p.lat, p.lng)));
        if (pts.some((p) => !p)) return;

        const [tl, tr, br, bl]: any = pts;

        // Compute center
        const centerX = (tl.x + tr.x + br.x + bl.x) / 4;
        const centerY = (tl.y + tr.y + br.y + bl.y) / 4;

        // Compute width and height as average of top/bottom and left/right edges
        const width = Math.sqrt((tr.x - tl.x) ** 2 + (tr.y - tl.y) ** 2);
        const height = Math.sqrt((bl.x - tl.x) ** 2 + (bl.y - tl.y) ** 2);

        this.div_.style.width = `${width}px`;
        this.div_.style.height = `${height}px`;
        this.div_.style.left = `${centerX - width / 2}px`;
        this.div_.style.top = `${centerY - height / 2}px`;
        this.div_.style.transform = `rotate(${rotation}deg)`;
      }

      onRemove() {
        if (this.div_) {
          this.div_.parentNode?.removeChild(this.div_);
          this.div_ = null;
        }
      }
    }

    const overlay = new CustomOverlay();
    overlay.setMap(map);

    return () => overlay.setMap(null);
  }, [map, polygon, imageUrl, opacity, rotation]);

  return null;
}
